import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ControlType } from '@/constants/institution';
import type { InstitutionFuturesDecisionMaker } from '@/features/institution/futures/decision-maker/schema';
import type { InstitutionFuturesExecution } from '@/features/institution/futures/execution/schema';
import { useInstitutionDetailsAnswers } from '@/features/institution/hooks/useInstitutionDetails';
import { generateAlgorithmId } from '@/features/institution/utils';

export type InstitutionFuturesAlgorithmType =
  | Extract<InstitutionFuturesDecisionMaker, { decisionMaker: typeof ControlType.ALGORITHM }>
  | Extract<InstitutionFuturesExecution, { execution: typeof ControlType.ALGORITHM }>;

type InstitutionFuturesAlgorithmOrDefaults = InstitutionFuturesAlgorithmType | Partial<InstitutionFuturesAlgorithmType>;

const InstitutionFuturesAlgorithm = () => {
  const { data: institutionDetailsAnswers } = useInstitutionDetailsAnswers();
  const form = useFormContext<InstitutionFuturesAlgorithmOrDefaults>();
  const { defaultValues } = form.formState;
  const { t } = useTranslation();
  const isManualEntry = useRef(false);
  const companyName = institutionDetailsAnswers?.company?.companyName;
  const algorithmId = form.watch('algorithm.algorithmId');
  const defaultAlgorithmId = defaultValues?.algorithm?.algorithmId;

  useEffect(() => {
    if (!algorithmId && !defaultAlgorithmId && !isManualEntry.current) form.setValue('algorithm.algorithmId', generateAlgorithmId(companyName ?? ''));
  }, [algorithmId, companyName, defaultAlgorithmId, form]);

  return (
    <FormField
      control={form.control}
      name="algorithm"
      render={() => (
        <FormField
          control={form.control}
          name="algorithm.algorithmId"
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel>{t('institutionDetails.algorithm.algorithmId.title')}</FormLabel>
              <FormDescription className="whitespace-pre-line">{t('institutionDetails.algorithm.algorithmId.description')}</FormDescription>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('institutionDetails.algorithm.placeholders.algorithmId')}
                  value={field.value ?? ''}
                  onInput={() => {
                    isManualEntry.current = true;
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    />
  );
};
InstitutionFuturesAlgorithm.displayName = 'InstitutionFuturesAlgorithm';

export default InstitutionFuturesAlgorithm;
